import { IntegrationServices } from '@constants/integration';

import type { MergeLinkIntegrations } from '.cache/__types__';

export type CrmUpgradeFlow = 'upgrade' | 'reconnect';

export interface UpgradeableIntegration {
  service:
    | IntegrationServices.HUBSPOT
    | IntegrationServices.SALESFORCE
    | IntegrationServices.MS_DYNAMICS_SALES
    | IntegrationServices.ZOHO_CRM
    | IntegrationServices.MONDAY
    | IntegrationServices.PIPEDRIVE;
  source: 'legacy' | 'merge' | 'native';
  flow: CrmUpgradeFlow;
}

interface Flags {
  canUseHubspotV2: boolean;
  canUseSalesforceV2: boolean;
  canUseMsDynamicsSalesV2: boolean;
  canUseZohoV2: boolean;
  canUsePipedriveV2: boolean;
}

const UPGRADEABLE_SOURCES = new Set(['legacy', 'merge']);

/** Native-only CRMs: nothing to upgrade from and no v2 flag, so only reconnect applies. */
const NATIVE_ONLY_SERVICES = new Set<IntegrationServices>([IntegrationServices.MONDAY]);

const RECONNECT_SCOPE_STATUSES = new Set(['MISSING_SCOPES', 'TOKEN_EXPIRED']);

/** Maps MergeLinkIntegrations (uppercase) to IntegrationServices (lowercase) */
const MERGE_TO_SERVICE: Record<string, IntegrationServices> = {
  HUBSPOT: IntegrationServices.HUBSPOT,
  SALESFORCE: IntegrationServices.SALESFORCE,
  MS_DYNAMICS_SALES: IntegrationServices.MS_DYNAMICS_SALES,
  ZOHO_CRM: IntegrationServices.ZOHO_CRM,
  PIPEDRIVE: IntegrationServices.PIPEDRIVE,
};

/** Which IntegrationServices values have a v2 native equivalent */
const V2_FLAG_BY_SERVICE: Partial<Record<IntegrationServices, keyof Flags>> = {
  [IntegrationServices.HUBSPOT]: 'canUseHubspotV2',
  [IntegrationServices.SALESFORCE]: 'canUseSalesforceV2',
  [IntegrationServices.MS_DYNAMICS_SALES]: 'canUseMsDynamicsSalesV2',
  [IntegrationServices.ZOHO_CRM]: 'canUseZohoV2',
  [IntegrationServices.PIPEDRIVE]: 'canUsePipedriveV2',
};

/**
 * Normalize a service string to an IntegrationServices value.
 *
 * Handles both:
 * - IntegrationServices / ServiceEnum values (lowercase, e.g. 'hubspot')
 * - MergeLinkIntegrations values (uppercase, e.g. 'HUBSPOT')
 */
function toIntegrationService(service: string): IntegrationServices | undefined {
  // Check if it's already a valid IntegrationServices value (lowercase)
  if (Object.values(IntegrationServices).includes(service as IntegrationServices)) {
    return service as IntegrationServices;
  }
  // Check if it's a MergeLinkIntegrations value (uppercase)
  return MERGE_TO_SERVICE[service];
}

/**
 * Determines which CRM integrations need the upgrade UX, and which flow to run:
 * - 'upgrade': source is 'legacy' or 'merge' and the service has a native v2
 * - 'reconnect': already native, but the connection must be re-authorized (missing
 *   newly required scopes, or a refresh token that no longer works)
 *
 * Both require the corresponding v2 feature flag to be enabled, except for
 * native-only services which have no such flag.
 */
export function getUpgradeableIntegrations(
  integrations: Array<{ service: string; source?: string | null; scopeStatus?: string | null }>,
  flags: Flags,
): UpgradeableIntegration[] {
  const result: UpgradeableIntegration[] = [];

  for (const integration of integrations) {
    const source = integration.source;
    if (!source) {
      continue;
    }

    const service = toIntegrationService(integration.service);
    if (!service) {
      continue;
    }

    const needsReconnect =
      source === 'native' && RECONNECT_SCOPE_STATUSES.has(integration.scopeStatus ?? '');

    if (NATIVE_ONLY_SERVICES.has(service)) {
      if (needsReconnect) {
        result.push({
          service: service as UpgradeableIntegration['service'],
          source: 'native',
          flow: 'reconnect',
        });
      }
      continue;
    }

    const flagKey = V2_FLAG_BY_SERVICE[service];
    if (!flagKey || !flags[flagKey]) {
      continue;
    }

    if (UPGRADEABLE_SOURCES.has(source)) {
      result.push({
        service: service as UpgradeableIntegration['service'],
        source: source as 'legacy' | 'merge',
        flow: 'upgrade',
      });
    } else if (needsReconnect) {
      result.push({
        service: service as UpgradeableIntegration['service'],
        source: 'native',
        flow: 'reconnect',
      });
    }
  }

  return result;
}

interface GetServiceUpgradeFlowArgs {
  service: IntegrationServices | MergeLinkIntegrations | string;
  /** Source of the rendered row — a legacy entry must not flag a native card, and vice versa. */
  source: UpgradeableIntegration['source'];
  upgradeableIntegrations: UpgradeableIntegration[];
}

/** The upgrade flow a service's connection should run, or undefined when none applies. */
export function getServiceUpgradeFlow({
  service,
  source,
  upgradeableIntegrations,
}: GetServiceUpgradeFlowArgs): CrmUpgradeFlow | undefined {
  const normalized = toIntegrationService(service);
  if (!normalized) {
    return undefined;
  }
  return upgradeableIntegrations.find((i) => i.service === normalized && i.source === source)?.flow;
}
